/**
 * Resolves the Hive API base URL + third-party auth ids from Vite env.
 * The base URL targets the server's same-origin /v1 proxy (server/routes/api-proxy.ts), which
 * injects the app key server-side (see APP_KEY in .env.local).
 */
export interface MuhahaConfig {
	baseUrl: string;
	/** Unused by the proxy (server injects the real key); required by HiveClient's type. */
	appKey: string;
	/** Google OAuth Web client id. Empty = Google sign-in disabled (button hidden). */
	googleClientId: string;
	/** Apple Services ID. Empty = Apple sign-in disabled (button hidden). */
	appleClientId: string;
	/** Apple Sign in redirect URI. Defaults to current origin when omitted. */
	appleRedirectUri: string;
	/** Sensors Analytics ingestion endpoint. Empty = analytics disabled. */
	sensorsServerUrl: string;
	/** Explicit kill switch; defaults to enabled when a server URL is present. */
	sensorsEnabled: boolean;
	/** Deployment label attached to analytics events. */
	appEnv: string;
	/** Build-time capability ceiling. Placements also require valid remote config and consent. */
	imaEnabled: boolean;
	/** Comma-separated HTTPS hosts allowed for remote IMA tags. */
	imaAllowedTagHosts: string;
}

const DEFAULT_BASE_URL = "/v1";

function env(key: string): string | undefined {
	const bag = (import.meta as { env?: Record<string, string | undefined> }).env;
	return bag ? bag[key] : undefined;
}

/**
 * Paid economy: Stripe web top-up → Mubux → credit packs → in-playable purchases.
 * Everything behind it was cut for the credits-only launch (f566d1d), so it stays
 * OFF unless a build opts in — a deploy that forgets the flag ships the launch
 * economy rather than a half-wired payment surface. Set in `.env.int` only.
 *
 * Build-time constant, read once at module load: components branch on it directly.
 */
export const PAID_ECONOMY_ENABLED = (() => {
	const value = env("VITE_ENABLE_PAID_ECONOMY");
	return value === "true" || value === "1";
})();

export function resolveConfig(): MuhahaConfig {
	const baseUrl = env("VITE_BACKEND_BASE_URL") ?? DEFAULT_BASE_URL;
	const sensorsServerUrl = env("VITE_SENSORS_SERVER_URL") ?? "";
	const sensorsEnabled = env("VITE_SENSORS_ENABLED");
	const imaEnabled = env("VITE_IMA_ENABLED");
	return {
		baseUrl: resolveBaseUrl(baseUrl),
		appKey: "",
		googleClientId: env("VITE_GOOGLE_CLIENT_ID") ?? "",
		appleClientId: env("VITE_APPLE_CLIENT_ID") ?? "",
		appleRedirectUri: env("VITE_APPLE_REDIRECT_URI") ?? "",
		sensorsServerUrl,
		sensorsEnabled:
			sensorsEnabled === undefined
				? Boolean(sensorsServerUrl)
				: sensorsEnabled !== "false" && sensorsEnabled !== "0",
		appEnv: env("VITE_APP_ENV") ?? env("MODE") ?? "development",
		imaEnabled: imaEnabled === "true" || imaEnabled === "1",
		imaAllowedTagHosts: env("VITE_IMA_ALLOWED_TAG_HOSTS") ?? "pubads.g.doubleclick.net,securepubads.g.doubleclick.net",
	};
}

/**
 * Resolves to an absolute same-origin URL, and *only* same-origin.
 *
 * Auth rides on a same-origin session cookie now: a cross-origin base would skip the proxy
 * that injects the bearer token AND suppress the cookie (fetch defaults to
 * credentials: "same-origin"), signing every user out with no error anywhere. That used to be
 * a harmless override — it is a kill switch now, so a cross-origin value is refused rather
 * than honoured.
 */
function resolveBaseUrl(baseUrl: string): string {
	if (typeof window === "undefined") return baseUrl;
	const resolved = new URL(baseUrl, window.location.origin);
	if (resolved.origin !== window.location.origin) {
		console.error(
			`[config] ignoring cross-origin VITE_BACKEND_BASE_URL (${resolved.origin}): ` +
				"the session cookie and the /v1 proxy are same-origin only",
		);
		return new URL(DEFAULT_BASE_URL, window.location.origin).toString().replace(/\/+$/, "");
	}
	return resolved.toString().replace(/\/+$/, "");
}
